import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import Leaflet, { LeafletEventHandlerFnMap } from 'leaflet';
import React, { FC, memo, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, TileLayer, useMap } from 'react-leaflet';
import { roundCoordinate } from 'src/utils/coordinate.util';

type Props = {
  dialogIsOpen: boolean,
  closeDialog: () => void;
  loading: boolean;
  lat: string;
  lng: string;
  updateLatLng: (lat: string, lng: string) => void;
  isOrder?: boolean;
}

// 小数 6 桁へ四捨五入してから文字列にする。Number を経由するので
// 35.700000 のような末尾 0 は落ちる（logposeTech/log_web_issues#333）
const latlngToString = (num: number) => roundCoordinate(num).toString();

// ペーストされた 1 要素を入力欄へ入れる文字列に直す。
// Why 有効な数値のときだけ丸める: Number('') は 0、Number('abc') は NaN になるため、
// 無条件に丸めると経度欄に "0"、非数値に "NaN" が入ってしまう。丸められない値は
// ペーストされた文字列のまま残す（logposeTech/log_web_issues#333）
export const roundPastedCoordinate = (text: string): string => {
  const num = Number(text);
  if (text === '' || Number.isNaN(num)) return text;

  return latlngToString(num);
};

const CoordinationEditDialog: FC<Props> = memo(({
  dialogIsOpen,
  closeDialog,
  loading,
  lat,
  lng,
  updateLatLng,
  isOrder,
}) => {
  const initializeCenter:[number, number] = [35.6812144014183, 139.7672018734606];
  const markerRef = useRef<Leaflet.Marker>(null);

  const [position, setPosition] = useState<[number, number]>(initializeCenter);
  const [latitude, setLatitude] = useState<string>('');
  const [longitude, setLongitude] = useState<string>('');

  const icon = new Leaflet.Icon({
    iconUrl: '/static/images/markers/red.png',
    iconSize: [24, 42],
    iconAnchor: [12, 42],
    tooltipAnchor: [-12, -42],
  });

  const onClickClose = () => {
    closeDialog();
  };

  const onClickSave = () => {
    const marker = markerRef.current;
    updateLatLng(
      latlngToString(marker.getLatLng().lat),
      latlngToString(marker.getLatLng().lng),
    );
  };

  const resetPosition = () => {
    setLatitude(lat);
    setLongitude(lng);
  };

  const markerEventHandler: LeafletEventHandlerFnMap = {
    dragend: (e) => {
      const marker = markerRef.current;
      setLatitude(latlngToString(marker.getLatLng().lat));
      setLongitude(latlngToString(marker.getLatLng().lng));
    },
  };

  function ChangeMapCenter({ pos }) {
    const mapCenter = useMap();
    mapCenter.panTo((pos) as [number, number]);

    return null;
  }

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteText = e.clipboardData.getData('text/plain');
    if (pasteText.includes(',')) {
      const values = pasteText.split(',', 2).map((value) => value.trim());
      // Why 丸める: ここで丸めないと 7 桁が入力欄に入り、保存後に 6 桁へ変わって見える
      setLatitude(roundPastedCoordinate(values[0]));
      setLongitude(roundPastedCoordinate(values[1]));
    }
  };

  useEffect(() => {
    if (!(lat && lng)) return;

    setPosition([Number(lat), Number(lng)]);
    setLatitude(lat);
    setLongitude(lng);
  }, [lat, lng]);

  useEffect(() => {
    if (!latitude || !longitude) return;

    const pos: [number, number] = [Number(latitude), Number(longitude)];
    setPosition(pos);
  }, [latitude, longitude]);

  const titleLayer = useMemo(() => (
    <TileLayer
      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
    />
  ), []);

  return (
    <Dialog open={dialogIsOpen} maxWidth="md" fullWidth onClose={closeDialog}>
      <DialogTitle>
        マーカーを移動するか、緯度経度の数値を入力して調整してください
      </DialogTitle>
      <DialogContent>
        <Stack direction="row" gap={1}>
          <TextField
            margin="normal"
            type="number"
            variant="standard"
            label="緯度"
            id="latitude"
            name="latitude"
            value={latitude}
            onChange={(e) => setLatitude(e.target.value)}
            onPaste={onPaste}
          />
          <TextField
            margin="normal"
            type="number"
            variant="standard"
            label="経度"
            id="longitude"
            name="longitude"
            value={longitude}
            onChange={(e) => setLongitude(e.target.value)}
            onPaste={onPaste}
          />
          <Button onClick={resetPosition}>戻す</Button>
        </Stack>
        <MapContainer
          style={{ height: '50vh' }}
          center={initializeCenter}
          zoom={17}
          scrollWheelZoom={false}
        >
          {titleLayer}
          <ChangeMapCenter pos={position} />
          <Marker
            draggable
            position={position}
            icon={icon}
            ref={markerRef}
            eventHandlers={markerEventHandler}
          />
        </MapContainer>
      </DialogContent>
      <DialogActions>
        {isOrder && (
          <Stack>
            <Typography variant="body1">同じ住所の同一日付の案件、同じ住所の地点マスタにも反映されます。</Typography>
          </Stack>
        )}
        <Button onClick={onClickClose} disabled={loading}>キャンセル</Button>
        <Button onClick={onClickSave} variant="contained" disabled={loading}>
          保存する
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default CoordinationEditDialog;
